import get from 'lodash/get';
import { message } from 'antd';
import { call, put, select, take, takeLeading, takeLatest } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import moment from 'moment';
import Auth from '@aws-amplify/auth';
import { Pool, refreshSession, getIdToken, getTokenExpiresIn } from '$app/auth';
import { api } from '$app/api';
import io from '$app/utils/sockets';
import { hitKeyESC } from '$app/utils/util';
import {
  clearState,
  bookingLoaded,
  authUserSuccess,
  bookingError,
  chargePaymentError,
  chargePaymentSuccess,
  chargeInstalmentSuccess,
  chargeInstalmentError,
  addNoteSuccess,
  patchBookingSuccess,
  reloadBooking as reloadBookingAction,
  updateGarageBillStatusSuccess,
  updateGarageBillStatusFailure,
  getGarageBillPdfUrlSuccess,
  getGarageBillPdfUrlFailure,
  bookingChanged,
} from './actions';
import { loadWorkCompleteReadiness } from './ServiceSheetMissingAlert/logic';
import {
  TOKEN_EXPIRED,
  PATCH_BOOKING,
  CANCEL_BOOKING,
  CHARGE_PAYMENT,
  CHARGE_INSTALMENT,
  ADD_NOTE,
  LOAD_BOOKING,
  EDIT_GARAGE_BILL_STATUS_REQUEST,
  GARAGE_BILL_PDF_URL_REQUEST,
  GARAGE_BILL_PDF_URL_SUCCESS,
  GARAGE_BILL_PDF_URL_FAILURE,
  RELOAD_BOOKING,
} from './constants';
import { getBookingUuid, getAuthUserSelector, getBooking as selectBooking } from './selectors';
import { attachRule, prioritizeRules } from '$app/shared/helpers/garageTimetable';

function* getAcceptedInvitesForDay(garageIds, collectionDate) {
  if (!garageIds.length) return [];
  const start = moment(collectionDate).startOf('day').format('YYYY-MM-DDTHH:mm');
  const end = moment(collectionDate).endOf('day').format('YYYY-MM-DDTHH:mm');
  const { data } = yield call(
    api,
    `/job-invitations/accepted?garageIds=${garageIds}&startDate=${start}&endDate=${end}`
  );
  return data;
}

function* getTimetableRules(garageIds) {
  const { data } = yield call(api, `/timetable/rules`, {
    method: 'post',
    data: {
      eventType: 'BatchGetTimetableRulesRequest',
      body: { timetableIds: garageIds },
    },
  });
  return data[0];
}

export function* getBooking(props) {
  const { bookingId } = props.match.params;

  try {
    console.info('Fetching booking %s', bookingId);
    const { data } = yield call(api, `bookings/${bookingId}/all`);

    const garageInvites = get(data, 'garageInvites', []);

    // eslint-disable-next-line no-restricted-syntax
    for (const invite of garageInvites) {
      if (['pending', 'review', 'accepted', 'cancelled', 'declined'].includes(invite.status)) {
        const { garageId } = invite;
        const garageAuth = yield call(api, `garages/${garageId}/authLink`, {
          method: 'get',
          params: { page: 'booking' },
        });

        const autoLoginLink = garageAuth.data.autoLoginLink.replace('{jobInvitationId}', invite.invitationId);

        invite.garage = Object.assign(invite.garage, { autoLoginLink });
      }
    }
    const collectionDate = get(data, 'collectionDateTime', null);
    const timetableIdsFromInvites = [...new Set(data.garageInvites.map(({ garage }) => garage.timetableId))];
    const garageIdsFromInvites = [...new Set(data.garageInvites.map(({ garageId }) => garageId))];

    let bulkTimetableRules;

    try {
      const ttRules = yield* getTimetableRules(timetableIdsFromInvites);
      bulkTimetableRules = ttRules;
    } catch (error) {
      console.error(error);
      bulkTimetableRules = [];
    }

    const acceptedInvites = yield* getAcceptedInvitesForDay(garageIdsFromInvites, collectionDate);

    data.garageInvites.forEach((invite, i) => {
      const dayTimetable = prioritizeRules(
        bulkTimetableRules[invite.garage.timetableId]
          .map((rule) => attachRule(collectionDate, rule))
          .filter((rule) => rule)
      );
      const allocationData = acceptedInvites.find(({ garageId }) => garageId === invite.garageId);
      const garageCapacity = {
        currentAllocation: get(allocationData, 'acceptedBookings', 0),
        currentCnd: get(allocationData, 'garageCollectionDeliveryCount', 0),
        maxCnd: get(dayTimetable, 'maxCnd', 0),
        maxAllocation: get(dayTimetable, 'maxAllocation', 0),
      };
      data.garageInvites[i] = { ...data.garageInvites[i], ...garageCapacity };
    });

    const bookingWithReadiness = yield* loadWorkCompleteReadiness(data, api);

    yield put(bookingLoaded(bookingWithReadiness));
  } catch (err) {
    yield put(bookingError(err));
  }
}

/**
 * Get authenticated user from Amplify Auth instead of CognitoUserPool
 * Amplify v5 manages auth session differently than raw amazon-cognito-identity-js
 */
export function* getAuthUser(props) {
  try {
    const amplifyUser = yield call([Auth, Auth.currentAuthenticatedUser], { bypassCache: false });
    const groups = get(amplifyUser, ['signInUserSession', 'idToken', 'payload', 'cognito:groups'], []);
    const authUser = {
      username: amplifyUser.username,
      email: amplifyUser.attributes?.email,
      name: amplifyUser.attributes?.given_name,
      surname: amplifyUser.attributes?.family_name,
      groups,
    };
    yield put(authUserSuccess(authUser));
  } catch (err) {
    yield put(bookingError(err));
  }
}

const shouldReloadBookingAfterPatch = (garageStatus) => garageStatus === 'CUSTOMER_REVIEW';

export function* patchBooking(props, action) {
  const { bookingId } = props.match.params;
  const { idempotencyKey } = action;

  try {
    const apiConfig = {
      method: 'patch',
      data: action.patch,
    };

    // pass idempotency key to headers if provided (for cancellations with fees)
    if (idempotencyKey) {
      apiConfig.headers = {
        'Idempotency-Key': idempotencyKey,
      };
    }

    const { data } = yield call(api, `bookings/${bookingId}`, apiConfig);
    const bookingWithReadiness = yield* loadWorkCompleteReadiness(data, api);
    yield put(bookingLoaded(bookingWithReadiness));
    yield put(patchBookingSuccess());
    if (action.patch.collectionAddressId || action.patch.deliveryAddressId) {
      yield put(bookingChanged(true));
    }

    if (shouldReloadBookingAfterPatch(action.patch.garageStatus)) {
      yield* reloadBooking(props);
    }
  } catch (err) {
    yield put(bookingError(err));
  }
}

function* addNote(props, action) {
  try {
    const bookingUuid = yield select(getBookingUuid);
    const authUser = yield select(getAuthUserSelector);

    const { note } = action;
    if (authUser) {
      note.generatedBy = `${authUser.name} ${authUser.surname}`;
    }
    note.ownerId = bookingUuid;
    note.ownerType = 'booking';

    const { data } = yield call(api, `/bookings/${bookingUuid}/notes`, {
      method: 'post',
      data: note,
    });
    yield put(addNoteSuccess(data));
  } catch (err) {
    yield put(bookingError(err));
  }
}

function* cancelBooking(props, action) {
  const { bookingId } = props.match.params;
  const { idempotencyKey, note, patch } = action;

  try {
    const apiConfig = {
      method: 'patch',
      data: patch,
    };

    if (idempotencyKey) {
      apiConfig.headers = {
        'Idempotency-Key': idempotencyKey,
      };
    }

    const { data: bookingData } = yield call(api, `bookings/${bookingId}`, apiConfig);
    const bookingWithReadiness = yield* loadWorkCompleteReadiness(bookingData, api);
    yield put(bookingLoaded(bookingWithReadiness));
    yield put(patchBookingSuccess());

    const bookingUuid = yield select(getBookingUuid);
    const authUser = yield select(getAuthUserSelector);
    const cancellationNote = {
      ...note,
      ownerId: bookingUuid,
      ownerType: 'booking',
    };

    if (authUser) {
      cancellationNote.generatedBy = `${authUser.name} ${authUser.surname}`;
    }

    const { data } = yield call(api, `/bookings/${bookingUuid}/notes`, {
      method: 'post',
      data: cancellationNote,
    });
    yield put(addNoteSuccess(data));
    yield put(reloadBookingAction());
  } catch (err) {
    yield put(bookingError(err));
  }
}

function* chargePayment(action) {
  const { idempotencyKey } = action;
  const booking = yield select(selectBooking);

  try {
    const { data } = yield call(api, `bookings/${booking.get('bookingId')}/take-payment`, {
      method: 'post',
      data: {},
      headers: {
        'Idempotency-Key': idempotencyKey,
      },
    });
    yield put(chargePaymentSuccess(data));
  } catch (err) {
    yield put(chargePaymentError(err?.response?.data || err));
  }
}

function* setNewPaymentMethod(booking) {
  const { data: paymentMethod } = yield call(api, `customers/${booking.get('customerId')}/payment-methods`, {
    method: 'post',
    data: {
      ownerId: booking.get('bookingUuid'),
      ownerType: 'booking',
      token: 'splitit',
      processor: 'splitit',
    },
  });
  const { paymentId } = paymentMethod;
  yield call(api, `/bookings/${booking.get('bookingId')}/payment-method`, {
    method: 'put',
    data: { paymentMethodId: paymentId },
  });
}

function* chargeInstalment(action) {
  const { idempotencyKey } = action;
  const booking = yield select(selectBooking);

  try {
    yield* setNewPaymentMethod(booking);
    const { data } = yield call(api, `bookings/${booking.get('bookingId')}/take-payment`, {
      method: 'post',
      data: {},
      headers: {
        'Idempotency-Key': idempotencyKey,
      },
    });
    yield put(chargeInstalmentSuccess(data));
  } catch (err) {
    yield put(chargeInstalmentError(err?.response?.data || err));
  }
}

function* updateGarageBillStatus(props, action) {
  try {
    const updatedInvoice = yield call(api, 'garages/bills', { method: 'patch', data: action.invoiceData });
    yield put(updateGarageBillStatusSuccess(updatedInvoice.data[0].data[1]));
  } catch (err) {
    const message = 'There was an error updating the status. Please try later';
    yield put(updateGarageBillStatusFailure(err, message));
  }
}

function* getGarageBillPdfUrl({ data }) {
  const { garageId, bookingId } = data;
  try {
    const response = yield call(api, `garages/bills?garageId=${garageId}&bookingId=${bookingId}`);
    const [pdfUrl] = JSON.parse(get(response, 'data.body', []));
    yield put(getGarageBillPdfUrlSuccess({ garageId, bookingId, pdfUrl }));
  } catch (err) {
    const { message } = JSON.parse(get(err, 'data.body', {}));
    yield put(getGarageBillPdfUrlFailure(message || err));
  }
}

const openGarageBillPdfUrl = ({ data }) => window.open(data.pdfUrl, '_blank');
const failedGarageBillPdfUrl = ({ error }) => {
  message.error('Download garage invoice failed. Please try again later');
  throw new Error(error);
};

const bookingChangeHandler = (bookingId, emit) => (data) => {
  const { eventType, booking } = data;
  if (
    typeof eventType !== 'undefined' &&
    typeof booking !== 'undefined' &&
    typeof booking.bookingId !== 'undefined' &&
    eventType === 'BOOKING_CHANGE' &&
    booking.bookingId.toString() === bookingId
  )
    emit(data);
};

const jobInvitationChangeHandler = (bookingId, emit) => (data) => {
  const { eventType, newState } = data;
  if (
    typeof eventType !== 'undefined' &&
    typeof newState !== 'undefined' &&
    typeof newState.bookingId !== 'undefined' &&
    ['INVITATION_CREATED', 'INVITATION_UPDATED'].includes(eventType) &&
    newState.bookingId.toString() === bookingId
  )
    emit(data);
};

const vehicleChangeHandler = (bookingId, emit) => (data) => {
  // emit(data);
};

/**
 * Socket saga channel - https://redux-saga.js.org/docs/advanced/Channels.html
 */
const createSocketChannel = (socket, props, vehicleId, accessToken) =>
  eventChannel((emit) => {
    const { bookingId } = props.match.params;
    const topic = {
      bookingChange: `/bookings/${bookingId}`,
      jobInvitationChange: '/job-invitations',
      vehicleChange: `/vehicles/${vehicleId}`,
    };

    socket.on('connected', (data) => console.log('socket connected'));
    socket.on('error', (error) => console.error('socket error:', error));
    socket.on(topic.bookingChange, bookingChangeHandler(bookingId, emit));
    socket.on(topic.jobInvitationChange, jobInvitationChangeHandler(bookingId, emit));
    socket.on(topic.vehicleChange, vehicleChangeHandler(bookingId, emit));

    // close socket and refresh user session before token expires (-5s)
    const verifySession = setInterval(() => {
      if (getTokenExpiresIn(accessToken) >= 5000) return null;
      refreshSession().then(() => {
        socket.close();
        emit(TOKEN_EXPIRED);
      });
      return clearInterval(verifySession);
    }, 1000);

    // unsubscribe
    return () => {
      socket.off(topic.bookingChange, bookingChangeHandler);
      socket.off(topic.jobInvitationChange, bookingChangeHandler);
      socket.off(topic.vehicleChange, bookingChangeHandler);
    };
  });

function* reloadBooking(props) {
  hitKeyESC(); // make sure it escapes editing mode before booking reload

  yield* getAuthUser(props);
  yield* getBooking(props);
}

export default function* bookingSaga(props) {
  yield takeLatest(ADD_NOTE, addNote, props);
  yield takeLatest(PATCH_BOOKING, patchBooking, props);
  yield takeLatest(CANCEL_BOOKING, cancelBooking, props);
  yield takeLatest(CHARGE_PAYMENT, chargePayment);
  yield takeLatest(CHARGE_INSTALMENT, chargeInstalment);
  yield takeLatest(LOAD_BOOKING, getBooking, props);
  yield takeLatest(EDIT_GARAGE_BILL_STATUS_REQUEST, updateGarageBillStatus, props);

  yield takeLeading(GARAGE_BILL_PDF_URL_REQUEST, getGarageBillPdfUrl);
  yield takeLatest(GARAGE_BILL_PDF_URL_SUCCESS, openGarageBillPdfUrl);
  yield takeLatest(GARAGE_BILL_PDF_URL_FAILURE, failedGarageBillPdfUrl);

  yield takeLatest(RELOAD_BOOKING, reloadBooking, props);

  // this prevent displaying old booking
  yield put(clearState());

  yield* getAuthUser(props);
  yield* getBooking(props);

  /**
   * Socket Saga
   */
  const { bookingId } = props.match.params;
  const booking = yield select(selectBooking);
  const vehicleId = booking.get('vehicleId');
  let socketChannel;
  while (true) {
    if (!socketChannel) {
      const accessToken = yield call(getIdToken);
      socketChannel = yield call(
        createSocketChannel,
        io(accessToken, bookingId),
        props,
        vehicleId,
        accessToken
      );
    }
    const data = yield take(socketChannel); // read emit from socket channel
    if (!data) continue;
    if (data === TOKEN_EXPIRED) {
      // reset socket channel
      socketChannel.close();
      socketChannel = null;
      continue;
    }
    yield put(bookingChanged(true));
  }
}
